/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { z } from "zod";
import type { validatorCreateSchema } from "../schemas";
import type { ELEMENT_TYPES } from "../enums";
import _ from "lodash";
type Validator = z.infer<typeof validatorCreateSchema>;

export const findValidatorWithType = <T extends Validator["validator"]["type"]>(
  validators: Validator[],
  type: T,
) => {
  const val = validators.find((validator) => validator.validator.type === type);
  if (!val) {
    return null;
  }
  return val as typeof val & { validator: { type: T } };
};

export const isElementNotEmpty = (
  elementValue: string | File | FileList | Date | boolean | number,
  elementType: (typeof ELEMENT_TYPES)[number],
) => {
  if (_.isNull(elementValue) || _.isUndefined(elementValue)) {
    return false;
  }
  switch (elementType) {
    case "FILE":
      return (elementValue as File).size > 0;
    case "FILE_MULTIPLE":
      return (elementValue as FileList).length > 0;
    case "CHECKBOX":
      return !!elementValue;
    case "DATE":
      // Handle both Date objects and string representations of dates
      if (elementValue instanceof Date) {
        return !isNaN(elementValue.getTime());
      } else if (typeof elementValue === "string") {
        // Attempt to parse the string as a date and check its validity
        const date = new Date(elementValue);
        return !isNaN(date.getTime());
      }
      return false;
    case "NUMBER":
      return !_.isNaN(elementValue as number);
    case "TEXT_BOX":
    case "TEXT_AREA":
    case "SELECT":
      return (
        (typeof elementValue === "string"
          ? elementValue
          : (elementValue as number).toString()
        )?.trim()?.length > 0
      );
  }
  return true;
};

export const getTableValidator = () => {
  return z
    .array(
      z.object({
        columnId: z.string(),
        rowNumber: z.number(),
        value: z.string().or(z.boolean()),
      }),
    )
    .optional();
};

export const getZodValidator = (
  validators: Validator[],
  elementType: (typeof ELEMENT_TYPES)[number],
) => {
  if (validators.length === 0) {
    return z.any();
  }

  let allowedTypes = z.any();

  const hasRequired = findValidatorWithType(validators, "REQUIRED");
  const hasClientSideDynamic = findValidatorWithType(validators, "CLIENT_SIDE_DYNAMIC");
  // if a value has client side dynamic, by default we don't want to add a required validator to it, as it may not be rendered
  // the check if it's rendered needs to be written on the frontend, and on the backend we will just ignore the empty values
  if (!hasRequired || hasClientSideDynamic) {
    // @ts-expect-error
    allowedTypes = z.optional(allowedTypes);
  }

  return allowedTypes.superRefine((val, ctx) => {
    const hasRequired = findValidatorWithType(validators, "REQUIRED");
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (hasRequired && !isElementNotEmpty(val, elementType) && !hasClientSideDynamic) {
      ctx.addIssue({
        code: "custom",
        message: hasRequired.validator.customErrorMessage || "Acest camp este obligatoriu",
      });
    }

    const hasLength = findValidatorWithType(validators, "LENGTH");
    if (hasLength && typeof val === "string") {
      if (hasLength.validator.options?.min && val.length < hasLength.validator.options.min) {
        ctx.addIssue({
          code: "custom",
          message:
            hasLength.validator.customErrorMessage ||
            `Acest camp trebuie sa contina minim ${hasLength.validator.options.min} caractere`,
        });
      }
      if (hasLength.validator.options?.max && val.length > hasLength.validator.options.max) {
        ctx.addIssue({
          code: "custom",
          message:
            hasLength.validator.customErrorMessage ||
            `Acest camp trebuie sa contina maxim ${hasLength.validator.options.max} caractere`,
        });
      }
    }

    const hasRegex = findValidatorWithType(validators, "REGEX");
    if (hasRegex && typeof val === "string") {
      const regex = new RegExp(hasRegex.validator.options!.value!);
      if (!regex.test(val)) {
        ctx.addIssue({
          code: "custom",
          message: hasRegex.validator.customErrorMessage || `Acest câmp nu respectă formatul`,
        });
      }
    }

    const hasPersonalCode = findValidatorWithType(validators, "PERSONAL_CODE");
    if (hasPersonalCode && typeof val === "string") {
      if (!validateCNP(val)) {
        ctx.addIssue({
          code: "custom",
          message:
            hasPersonalCode.validator.customErrorMessage ||
            `Acest câmp nu respectă formatul pentru CNP`,
        });
      }
    }
  });
};

export function validateCNP(value: string) {
  const re = /^\d{1}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(0[1-9]|[1-4]\d| 5[0-2]|99)\d{4}$/;
  const control = "279146358279";
  let bigSum = 0,
    ctrlDigit = 0,
    i = 0;

  if (re.test(value)) {
    for (i = 0; i < 12; i++) {
      bigSum += Number(value[i]) * Number(control[i]);
    }
    ctrlDigit = bigSum % 11;
    if (ctrlDigit === 10) {
      ctrlDigit = 1;
    }

    if (ctrlDigit !== parseInt(value[12]!, 10)) {
      return false;
    } else {
      return true;
    }
  }
  return false;
}
